import { Dropdown, OverlayTrigger, Tooltip } from '@valid-eval/shared-react-components';
import { useUpdateMultipleStatus } from 'data/features/evaluationsHooks';
import { Phase } from 'data/features/phaseTypes';
import { TeamResult } from './types';
import Loading from 'components/Loading';

type ChangeStatusButtonProps = {
  onStatusChanged?: (status: number | null) => void;
  selectedRows: TeamResult[];
  phase: Phase;
  loading: boolean;
};

function ChangeStatusButton({
  onStatusChanged,
  selectedRows,
  phase,
  loading,
}: ChangeStatusButtonProps) {
  const { isSaving, updateMultipleStatus } = useUpdateMultipleStatus();
  const selectionCount = selectedRows.length;
  const disabled = selectionCount === 0 || isSaving || loading;

  const handleSelect = () => {
    const el = document.activeElement as HTMLElement | null;
    if (el && typeof el.blur === 'function') {
      el.blur();
    }
  };

  const handleChangeStatus = (status: number | null) => async () => {
    await updateMultipleStatus({
      phaseId: phase.id,
      evaluationIds: selectedRows.map((row) => row.id),
      status,
    });
    onStatusChanged?.(status);
  };

  const statuses = Object.values(phase.status_framework)
    .filter((status) => status.active)
    .sort((a, b) => b.order - a.order);

  return (
    <Dropdown onSelect={handleSelect}>
      <OverlayTrigger
        placement="bottom"
        delay={100}
        overlay={
          disabled && !(loading || isSaving) ? (
            <Tooltip>
              Select teams to change their status.
              <br />
              You can multi-select with the Shift key
            </Tooltip>
          ) : (
            <></>
          )
        }
      >
        <Dropdown.Toggle
          data-test-id="change-status-button"
          variant={'primary'}
          style={{ pointerEvents: 'all', cursor: disabled ? 'not-allowed' : 'pointer' }}
          className="ms-2 pe-3"
          disabled={disabled}
        >
          {loading || isSaving ? (
            <Loading autoSize />
          ) : (
            <>
              Change status
              <i className="fa-regular fa-angle-down ms-2"></i>
            </>
          )}
        </Dropdown.Toggle>
      </OverlayTrigger>
      <Dropdown.Menu>
        {statuses.map((status) => (
          <Dropdown.Item
            key={status.value}
            data-test-id={`change-status-option-${status.default_label}`}
            onClick={handleChangeStatus(status.value)}
          >
            {status.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default ChangeStatusButton;
